import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import {
  Button,
  CircularProgress,
  Snackbar,
  SnackbarContent,
  Stack,
  TextField,
} from "@mui/material";
import "./contact-page.css";

import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReCAPTCHA from "react-google-recaptcha";

function ContactPage() {
  const formRef = useRef<HTMLFormElement | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [formValid, setFormValid] = useState(false);
  const [formValues, setFormValues] = useState({
    form_name: "",
    form_email: "",
    form_tele: "",
    message: "",
  });

  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailFailed, setEmailFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const RECAPTCHA_SITE_KEY = "6Lf3d5grAAAAAA2hXUrARUpLOtOPwvNQFr6a4Uy6";

  const resetCaptcha = () => {
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  const resetForm = () => {
    setFormValues({
      form_name: "",
      form_email: "",
      form_tele: "",
      message: "",
    });
    resetCaptcha();
  };

  const sendEmail = async (e: any) => {
    e.preventDefault();

    if (!captchaToken) {
      setEmailFailed(true);
      return;
    }

    if (!e.target) {
      setEmailFailed(true);
      return;
    }

    try {
      setIsLoading(true);

      await emailjs.sendForm(
        "service_6e2xqna",
        "template_vnglbnc",
        e.target,
        "cWG3uHrMzUsLQTF4_"
      );

      setEmailSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      resetForm();
    } catch (error) {
      console.error("Email send failed:", error);
      setEmailFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setEmailSuccess(false);
    setEmailFailed(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptcha = (token: string | null) => {
    setCaptchaToken(token);
  };

  useEffect(() => {
    const isValid =
      formValues.form_name.trim() !== "" &&
      formValues.form_email.trim() !== "" &&
      formValues.message.trim() !== "" &&
      /\S+@\S+\.\S+/.test(formValues.form_email) &&
      captchaToken !== null;

    setFormValid(isValid);
  }, [formValues, captchaToken]);

  const clearForm = () => {
    resetForm();
  };

  return (
    <div id="contact">
      <Snackbar
        open={emailSuccess}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          style={{
            backgroundColor: "var(--purple_c)",
          }}
          message={
            <span id="client-snackbar" className="snackbar-message">
              <CheckBoxIcon />
              <b> See you soon!</b>{" "}
            </span>
          }
        />
      </Snackbar>
      <Snackbar
        open={emailFailed}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          style={{
            backgroundColor: "#e74c3c",
          }}
          message={
            <span id="client-snackbar" className="snackbar-message">
              <ErrorOutlineIcon />
              <b> Something went wrong. Please try again later.</b>{" "}
            </span>
          }
        />
      </Snackbar>

      <div className="global-title" data-aos="fade-up">
        Contact
      </div>
      <div className="global-subtitle" data-aos="fade-up">
        Get in Touch with Mudith
      </div>

      {isLoading ? (
        <div className="spinner">
          <CircularProgress size="100px" color="secondary" />
        </div>
      ) : (
        <form ref={formRef} onSubmit={sendEmail}>
          <div className="contact_form">
            <div className="contact_form_container">
              <TextField
                label="Name"
                name="form_name"
                required
                size="small"
                value={formValues.form_name}
                onChange={handleChange}
                data-aos="fade-up"
              />

              <TextField
                label="Email"
                name="form_email"
                type="email"
                required
                size="small"
                value={formValues.form_email}
                onChange={handleChange}
                data-aos="fade-up"
              />

              <TextField
                label="Mobile No."
                name="form_tele"
                size="small"
                value={formValues.form_tele}
                onChange={handleChange}
                data-aos="fade-up"
              />

              <TextField
                label="Message"
                name="message"
                multiline
                rows={5}
                required
                value={formValues.message}
                onChange={handleChange}
                data-aos="fade-up"
              />

              <div style={{ margin: "16px 0" }} data-aos="fade-up">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptcha}
                  onExpired={() => setCaptchaToken(null)}
                />
              </div>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="end"
                data-aos="fade-up"
              >
                <Button
                  onClick={clearForm}
                  variant="outlined"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  sx={{
                    borderColor: "var(--black_c)",
                    color: "var(--black_c)",
                  }}
                >
                  Clear
                </Button>

                <Button
                  type="submit"
                  value="Send"
                  color="secondary"
                  variant="contained"
                  endIcon={<SendIcon />}
                  disabled={!formValid || isLoading}
                  sx={{
                    backgroundColor: "var(--black_c)",
                    color: "var(--white_c)",
                  }}
                >
                  Send
                </Button>
              </Stack>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default ContactPage;